// strokeLogTimeline — download + validate + merge a room's stroke log
// (絵ログ T1 取得・整列 — growth-timelapse-share-2026-07-02.md §5.1 /
// §5.3.1 T0 実装結果).
//
// Pipeline:
//   1. `GET {dispatcher}/rooms/:roomId/stroke-log` → segment listing
//      (`{ segments: [{fromSeq, toSeq, url}], urlExpiresInSeconds }`,
//      presigned URLs, T0).
//   2. `planSegmentDownloads` — trim-range filter (segments wholly outside
//      the requested seq range are never downloaded) + deterministic order.
//   3. Parallel segment JSON downloads, each validated through
//      `strokeLogSegmentSchema` (`@oekaki/protocol` — the writer and this
//      reader share one schema so drift is impossible). Per-segment
//      failures degrade to skip + warn (version ≠ 1 / shape mismatch /
//      fetch error) — the T0 rule is that holes are tolerable, so one bad
//      segment must not kill the whole timelapse.
//   4. `mergeSegments` — flushedAtMs overlap resolution + seq ordering +
//      coverage/gap accounting (pure, unit-tested separately).
//
// Access path note: the dispatcher base URL comes from the same
// `readEnv().dispatcherUrl` the rest of the web package uses; this module
// takes it as a plain argument (composition root / caller resolves it)
// and deliberately does NOT extend `DispatcherClient` — that port is the
// room-lifecycle surface consumed app-wide, and widening it would ripple
// through every stub while this endpoint has exactly one consumer (the
// timelapse feature). Outcome-union style mirrors `DispatcherClient` /
// `SnapshotLoader` (no throws on expected failures).

import { type StrokeLogSegment, strokeLogSegmentSchema } from '@oekaki/protocol';
import type { Logger } from '../../application/ports/Logger.js';
import { collectLineageBaseRanges, findDocumentStart, MAX_LINEAGE_DEPTH } from './lineageSeed.js';
import {
  type MergedStrokeLog,
  mergeSegments,
  planSegmentDownloads,
  type SeqRange,
  type StrokeLogSegmentListEntry,
} from './segmentMerge.js';

/** Why a listed segment was dropped from the merge (diagnostic / UI warning). */
export type SkippedSegmentReason =
  | 'fetch-failed' // network error or non-2xx on the presigned GET
  | 'invalid-json' // body was not JSON
  | 'unsupported-version' // `version !== 1` (future writer — this reader is too old)
  | 'schema-mismatch'; // strokeLogSegmentSchema rejected the document

export interface SkippedSegment extends SeqRange {
  readonly reason: SkippedSegmentReason;
}

export interface StrokeLogTimeline extends MergedStrokeLog {
  /** Segments listed by the dispatcher but not merged (skip + warn rule). */
  readonly skippedSegments: readonly SkippedSegment[];
}

export type LoadStrokeLogTimelineOutcome =
  | { readonly outcome: 'ok'; readonly timeline: StrokeLogTimeline }
  /** Room unknown / deleted / invalid UUID (dispatcher 404・400). */
  | { readonly outcome: 'not-found' }
  /** Stroke log store not wired / listing failed server-side (503). */
  | { readonly outcome: 'unavailable' }
  | {
      readonly outcome: 'error';
      readonly cause: 'network' | 'invalid' | 'http-status';
      readonly status?: number;
    };

export interface LoadStrokeLogTimelineInput {
  /** Dispatcher base URL (`readEnv().dispatcherUrl`). Trailing `/` tolerated. */
  readonly dispatcherUrl: string;
  readonly roomId: string;
  /**
   * Optional pre-download trim (§5.2): segments entirely outside this
   * inclusive seq range are not downloaded. NOTE: the merged event list
   * may still contain events outside the range (a segment straddling the
   * boundary is downloaded whole) — `createTimelapseFrameSource` applies
   * the exact per-event trim.
   */
  readonly seqRange?: SeqRange;
  readonly signal?: AbortSignal;
  /**
   * 指定すると「新しいセグメントから読んで系譜の根で止める」モードになる
   * (荒らし復旧「人を外して戻す」、vandalism-recovery-ui-2026-09-27.md §3)。
   * `seqRange` とは併用しない (指定されても無視)。listing は従来どおり取得し、
   * 本体は終端側から {@link LINEAGE_DOWNLOAD_CONCURRENCY} 本ずつ降順に DL して、
   * 終端の文書の根 (全消し / restore マーカー) が見つかった時点で止める。restore
   * を跨ぐ系譜は復元元文書 (`restoredFromSeq` 以前) の根まで同じ要領で辿る。
   * 「読み込んだ窓の先頭」は根とみなさない (より古い未取得セグメントが残って
   * いる間は降り続ける。無くなって初めて「ログ先頭が根」)。
   */
  readonly lineage?: LineageLoadOptions;
  /**
   * 該当ルームの owner 用 admin_token (client 保持分)。渡すと **listing 要求のみ**
   * `Authorization: Bearer <adminToken>` を付ける (絵ログ共有をオーナー限定にした
   * consult 2026-07-08 案B — `GET /rooms/:roomId/stroke-log` は owner 認証必須)。
   * セグメント本体の DL は presigned R2 URL なので auth ヘッダは付けない。空文字 /
   * 省略時は無認証で撃つ (= visitor。通常はボタンが owner 限定なので到達しないが、
   * サーバは 401/403 を返し `error` outcome に落ちる = モーダルは固まらない)。
   */
  readonly adminToken?: string;
}

export interface LineageLoadOptions {
  /**
   * 終端 seq (inclusive)。過去キャンバス行の文書を対象にするとき = その行の endSeq。
   * これより新しいセグメントは DL しない。省略 = 現行文書 (最新まで)。
   */
  readonly endSeq?: number;
  /**
   * 現行文書 (`endSeq` 省略) の未 flush 分 (live tail) を取る hook。最新のセグメント
   * を読んだ直後に 1 回呼ぶ (引数 = 手元の R2 coverage の最大 toSeq)。返した
   * セグメントは系譜判定の対象になる (未 flush 分に全消し / restore があっても
   * 根を正しく見つけられる)。
   */
  readonly liveTail?: (sinceSeq: number) => Promise<StrokeLogSegment | null>;
}

/** 系譜モードの 1 ラウンドあたり並列 DL 数 (根が近ければ読み過ぎを 1 ラウンド分に抑える)。 */
export const LINEAGE_DOWNLOAD_CONCURRENCY = 4;

/** Narrow fetch shape so tests inject a stub (same convention as SnapshotLoader). */
export type FetchFn = (
  input: string,
  init?: { signal?: AbortSignal; headers?: Record<string, string> },
) => Promise<Response>;

export interface StrokeLogTimelineDeps {
  /** Default: `globalThis.fetch`. */
  readonly fetchFn?: FetchFn;
  readonly logger?: Logger;
}

export const defaultFetchFn: FetchFn = (input, init) => {
  const f = globalThis.fetch as
    | ((url: string, init?: RequestInit) => Promise<Response>)
    | undefined;
  if (f === undefined) throw new Error('globalThis.fetch is not available');
  return f.call(globalThis, input, init as RequestInit | undefined);
};

export const noopLogger: Logger = {
  debug: () => undefined,
  info: () => undefined,
  warn: () => undefined,
  error: () => undefined,
};

/** Listing body shape gate (mirror of dispatcher `StrokeLogListResponse`). */
const parseListing = (raw: unknown): StrokeLogSegmentListEntry[] | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const segments = (raw as Record<string, unknown>).segments;
  if (!Array.isArray(segments)) return null;
  const out: StrokeLogSegmentListEntry[] = [];
  for (const item of segments) {
    if (typeof item !== 'object' || item === null) return null;
    const r = item as Record<string, unknown>;
    if (typeof r.fromSeq !== 'number' || !Number.isFinite(r.fromSeq)) return null;
    if (typeof r.toSeq !== 'number' || !Number.isFinite(r.toSeq)) return null;
    if (typeof r.url !== 'string' || r.url.length === 0) return null;
    out.push({ fromSeq: r.fromSeq, toSeq: r.toSeq, url: r.url });
  }
  return out;
};

/** listing 取得の結果 (`loadStrokeLogTimeline` / 窓読み込みで共有)。 */
export type FetchStrokeLogListingOutcome =
  | { readonly outcome: 'ok'; readonly entries: StrokeLogSegmentListEntry[] }
  | Exclude<LoadStrokeLogTimelineOutcome, { outcome: 'ok' }>;

/**
 * `GET {dispatcher}/rooms/:roomId/stroke-log` (owner 認証: consult 2026-07-08 案B で listing は
 * owner 限定なので admin_token を Bearer で送る。セグメント本体 (presigned R2) には付けない)。
 */
export const fetchStrokeLogListing = async (
  input: {
    readonly dispatcherUrl: string;
    readonly roomId: string;
    readonly signal?: AbortSignal;
    readonly adminToken?: string;
  },
  fetchFn: FetchFn,
  logger: Logger,
): Promise<FetchStrokeLogListingOutcome> => {
  const baseUrl = input.dispatcherUrl.replace(/\/+$/, '');
  const listUrl = `${baseUrl}/rooms/${encodeURIComponent(input.roomId)}/stroke-log`;
  // owner 認証 (consult 2026-07-08 案B): listing は owner 限定エンドポイントなので
  // admin_token を Bearer で送る。セグメント本体 (presigned R2) には付けない。
  const listInit: { signal?: AbortSignal; headers?: Record<string, string> } = {};
  if (input.signal !== undefined) listInit.signal = input.signal;
  if (input.adminToken !== undefined && input.adminToken.length > 0) {
    listInit.headers = { Authorization: `Bearer ${input.adminToken}` };
  }
  let listRes: Response;
  try {
    listRes = await fetchFn(listUrl, listInit);
  } catch (cause) {
    logger.warn('strokeLogTimeline: listing network error', {
      err: cause instanceof Error ? cause.message : String(cause),
    });
    return { outcome: 'error', cause: 'network' };
  }
  if (listRes.status === 404 || listRes.status === 400) return { outcome: 'not-found' };
  if (listRes.status === 503) return { outcome: 'unavailable' };
  if (listRes.status !== 200) {
    logger.warn('strokeLogTimeline: listing unexpected http status', { status: listRes.status });
    return { outcome: 'error', cause: 'http-status', status: listRes.status };
  }
  let rawListing: unknown;
  try {
    rawListing = await listRes.json();
  } catch {
    logger.warn('strokeLogTimeline: listing 200 with invalid JSON body');
    return { outcome: 'error', cause: 'invalid', status: 200 };
  }
  const entries = parseListing(rawListing);
  if (entries === null) {
    logger.warn('strokeLogTimeline: listing body shape mismatch');
    return { outcome: 'error', cause: 'invalid', status: 200 };
  }
  return { outcome: 'ok', entries };
};

/**
 * Load + merge the room's stroke log into a replayable timeline.
 *
 * Returns `ok` with an empty timeline when the room simply has no
 * segments yet (log is T0-deploy-onward only — pre-existing rooms may
 * legitimately be empty); the UI distinguishes that from `not-found`.
 */
export const loadStrokeLogTimeline = async (
  input: LoadStrokeLogTimelineInput,
  deps: StrokeLogTimelineDeps = {},
): Promise<LoadStrokeLogTimelineOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const logger = deps.logger ?? noopLogger;

  // ── 1. Listing ────────────────────────────────────────────────────────────
  const listing = await fetchStrokeLogListing(input, fetchFn, logger);
  if (listing.outcome !== 'ok') return listing;
  const entries = listing.entries;

  // ── 2-3. Download + validate each segment (trim-filtered, parallel) ──────
  const lineage = input.lineage;
  const planned = lineage !== undefined ? [] : planSegmentDownloads(entries, input.seqRange);
  const skippedSegments: SkippedSegment[] = [];
  const segments: StrokeLogSegment[] = [];
  const download = async (batch: readonly StrokeLogSegmentListEntry[]): Promise<void> => {
    const results = await Promise.all(
      batch.map((entry) => downloadSegment(entry, fetchFn, input.signal, logger)),
    );
    for (const r of results) {
      if ('segment' in r) segments.push(r.segment);
      else skippedSegments.push(r.skipped);
    }
  };
  await download(planned);

  // ── 4. Merge (pure) ───────────────────────────────────────────────────────
  let merged = mergeSegments(segments);

  // ── 3'. 系譜モード: 終端側から降りて根で止める ─────────────────────────────
  if (lineage !== undefined) {
    const searchSeq = lineage.endSeq === undefined ? Number.POSITIVE_INFINITY : lineage.endSeq - 1;
    // 終端 seq 自身のイベントが境界 (全消し / restore) だと、その行の文書は 1 つ手前で
    // 終わる (`normalizeEndSeq`)。境界探索は常に `endSeq - 1` 以前で行えば、境界が
    // endSeq 自身でも別でも正しい根が得られる (非境界なら両者の結果は同じ)。
    const candidates = entries
      .filter((e) => lineage.endSeq === undefined || e.fromSeq <= lineage.endSeq)
      .sort((a, b) => (a.toSeq !== b.toSeq ? b.toSeq - a.toSeq : b.fromSeq - a.fromSeq));
    const fetched = new Set<string>();
    /** `maxSeq` 以下の未取得セグメントを新しい順に最大 N 本 DL。取れるものが無ければ false。 */
    const downloadNewer = async (maxSeq: number): Promise<boolean> => {
      const batch: StrokeLogSegmentListEntry[] = [];
      for (const e of candidates) {
        if (fetched.has(e.url) || e.fromSeq > maxSeq) continue;
        batch.push(e);
        if (batch.length >= LINEAGE_DOWNLOAD_CONCURRENCY) break;
      }
      if (batch.length === 0) return false;
      for (const e of batch) fetched.add(e.url);
      await download(batch);
      merged = mergeSegments(segments);
      return true;
    };
    /** `maxSeq` 以前の文書境界が見つかるまで (or 古いセグメントが尽きるまで) 降りる。 */
    const loadDownTo = async (maxSeq: number): Promise<void> => {
      for (;;) {
        const start = findDocumentStart(merged.events, maxSeq);
        if (start !== null && start.kind !== 'head') return;
        if (input.signal?.aborted === true) return;
        if (!(await downloadNewer(maxSeq))) return; // これ以上古いものが無い = ログ先頭が根
      }
    };

    await downloadNewer(searchSeq);
    if (lineage.endSeq === undefined && lineage.liveTail !== undefined) {
      let since = 0;
      for (const r of merged.coverage) if (r.toSeq > since) since = r.toSeq;
      const tail = await lineage.liveTail(since);
      if (tail !== null) {
        segments.push(tail);
        merged = mergeSegments(segments);
      }
    }
    let cursor = searchSeq;
    for (let depth = 0; depth < MAX_LINEAGE_DEPTH; depth++) {
      await loadDownTo(cursor);
      const start = findDocumentStart(merged.events, cursor);
      if (start === null || start.kind !== 'restore') break;
      if (start.marker.restoredFromSeq >= start.marker.seq) break;
      cursor = start.marker.restoredFromSeq; // 復元元文書の根まで続けて降りる
    }
  }

  // ── 4b. 系譜範囲の追加 DL (past-canvas §4.5-2 実装帰結 4) ──────────────────
  // trim 範囲内の `whiteboard.restore` マーカーは、可視範囲より前 (= trim
  // pre-filter で落ちた) 系譜範囲の再適用を要求する。マーカーの発見が DL 後に
  // なる鶏卵は「merge → マーカー走査 → 系譜範囲に交差する未取得セグメントを
  // 追加 DL → re-merge」の反復で解く (1 反復 ≒ チェーン 1 段の発見、上限 =
  // 深さ上限)。range 未指定なら全セグメント取得済み = 走査不要。取得失敗は
  // 既存どおり skip + 継続 (系譜土台が部分的に欠けるだけで例外にしない)。
  // NOTE: 追加取得により coverage が飛び石になり、間 (= 意図的に飛ばした中間
  // 文書) が `gaps` に載る — 診断情報としては過剰報告だが replay には無害。
  // 本番経路 (encodeJob は seed のため下端を広げて全取得 / 編集モーダルは全 DL)
  // ではこのループは no-op で、狭い range 指定の直接呼び出しだけが通る。
  if (lineage === undefined && input.seqRange !== undefined) {
    const fetched = new Set(planned.map((e) => e.url));
    for (let round = 0; round < MAX_LINEAGE_DEPTH; round++) {
      const needed = collectLineageBaseRanges(merged.events, input.seqRange);
      if (needed.length === 0) break;
      const extra = planSegmentDownloads(
        entries.filter(
          (e) =>
            !fetched.has(e.url) && needed.some((r) => e.toSeq >= r.fromSeq && e.fromSeq <= r.toSeq),
        ),
      );
      if (extra.length === 0) break; // 収束 (必要範囲はすべて取得済み or 存在しない)
      for (const e of extra) fetched.add(e.url);
      await download(extra);
      merged = mergeSegments(segments);
    }
  }

  return {
    outcome: 'ok',
    timeline: { ...merged, skippedSegments },
  };
};

export type SegmentDownloadResult = { segment: StrokeLogSegment } | { skipped: SkippedSegment };

export const downloadSegment = async (
  entry: StrokeLogSegmentListEntry,
  fetchFn: FetchFn,
  signal: AbortSignal | undefined,
  logger: Logger,
): Promise<SegmentDownloadResult> => {
  const skip = (
    reason: SkippedSegmentReason,
    ctx?: Record<string, unknown>,
  ): SegmentDownloadResult => {
    logger.warn('strokeLogTimeline: segment skipped', {
      fromSeq: entry.fromSeq,
      toSeq: entry.toSeq,
      reason,
      ...ctx,
    });
    return { skipped: { fromSeq: entry.fromSeq, toSeq: entry.toSeq, reason } };
  };

  let res: Response;
  try {
    res = await fetchFn(entry.url, signal !== undefined ? { signal } : {});
  } catch (cause) {
    return skip('fetch-failed', {
      err: cause instanceof Error ? cause.message : String(cause),
    });
  }
  if (!res.ok) return skip('fetch-failed', { status: res.status });

  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return skip('invalid-json');
  }

  // Distinguish "writer newer than this reader" from a generic shape bug —
  // the T0 rule calls both out for skip + warn, but the UI copy differs
  // (「アプリの更新が必要です」 vs 診断ログ行き).
  if (typeof raw === 'object' && raw !== null && (raw as Record<string, unknown>).version !== 1) {
    return skip('unsupported-version', {
      version: (raw as Record<string, unknown>).version,
    });
  }
  const parsed = strokeLogSegmentSchema.safeParse(raw);
  if (!parsed.success) return skip('schema-mismatch');
  return { segment: parsed.data };
};
